import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useSearchParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { FiClipboard as ClipboardList, FiAlertTriangle as AlertTriangle, FiInfo } from 'react-icons/fi';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ClientProvider } from './contexts/ClientContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { FilterProvider } from './contexts/FilterContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ApiHealthProvider } from './contexts/ApiHealthContext';
import { DemoModeProvider } from './contexts/DemoModeContext';
import QuotaWarningBanner from './components/common/QuotaWarningBanner';
import { apiCall } from './services/realApi';
import { usePermissions } from './hooks/usePermissions';
import { useModuleAccess } from './hooks/useModuleAccess';
import { PERMISSIONS } from './utils/permissions';
import Login from './pages/Login';
import SignUp from './pages/SignUp';
import SetPassword from './pages/SetPassword';
import ResetPassword from './pages/ResetPassword';
import ForgotPassword from './pages/ForgotPassword';
import ApproveUser from './pages/ApproveUser';
import SSOCallbackPage from './pages/SSOCallbackPage';
import Navigation from './components/Navigation';
import ProtectedRoute from './components/common/ProtectedRoute';
import HubDashboardPage from './features/hub/dashboard/HubDashboardPage';
import HealthDashboardPage from './features/hub/health/HealthDashboardPage';
import DashboardMarketplace from './components/dashboard/DashboardMarketplace';
import DashboardDesigner from './components/dashboard/DashboardDesigner';
import DashboardViewer from './components/dashboard/DashboardViewer';
import { ProjectsPage } from './features/hub/projects';
import WorkloadsPage from './features/hub/workloads/WorkloadsPage';
import { FundingPage } from './features/hub/funding';
import { AssessmentPage } from './features/hub/assessment';
import { AdminPage } from './features/settings/admin';
import { AccessManagementPage } from './features/settings/access-management';
import { AWSEnvironmentsPage } from './features/settings/aws-environments';
import TaskExecutionsPage from './features/hub/tasks/TaskExecutionsPage';
import { FinOpsPage, EnvironmentsPage } from './features/cloudops';
import TaggingPage from './features/cloudops/TaggingPage';
import GuardrailsPage from './features/cloudops/GuardrailsPage';
import EnvironmentsManagementPage from './features/cloudops/pages/EnvironmentsManagementPage';
import EnvironmentDetailsPage from './features/cloudops/pages/EnvironmentDetailsPage';
import MonitoringEnvironmentDetailPage from './features/cloudops/pages/MonitoringEnvironmentDetailPage';
import ViolationsPage from './features/cloudops/pages/ViolationsPage';
import ViolationDetailsPage from './features/cloudops/pages/ViolationDetailsPage';
import PoliciesPage from './features/cloudops/pages/PoliciesPage';
import CreateEditPolicyPage from './features/cloudops/pages/CreateEditPolicyPage';
import ResourcesPage from './features/cloudops/pages/ResourcesPage';
import ScanDetailsTimelinePage from './features/cloudops/pages/ScanDetailsTimelinePage';
import AnomaliesPage from './features/cloudops/pages/AnomaliesPage';
import CostRecommendationsPage from './features/cloudops/pages/CostRecommendationsPage';
import ComputeOptimizationPage from './features/cloudops/ComputeOptimizationPage';
import NotFound from './pages/NotFound';
import TrialInfoPage from './pages/TrialInfoPage';
import ConnectAwsInfoPage from './pages/ConnectAwsInfoPage';
import ChangelogPage from './pages/ChangelogPage';
import MarketplaceRegisterPage from './pages/MarketplaceRegisterPage';
import EulaPage from './pages/EulaPage';

// Layout components
import HubLayout from './features/hub/components/HubLayout';
import HealthLayout from './features/hub/health/components/HealthLayout';
import CloudOpsLayout from './features/cloudops/components/CloudOpsLayout';
import AIOperationsLayout from './features/ai-operations/components/AIOperationsLayout';
import DataOpsLayout from './features/dataops/components/DataOpsLayout';

// Hub components
import Support from './features/hub/components/Support';
import Users from './features/hub/components/Users';
import Clients from './features/hub/components/Clients';

// AI Operations pages (merged GenAIOps + AgentOps)
import { AIOperationsDashboardPage } from './features/ai-operations/dashboard';
import ObservabilityPage from './features/ai-operations/genaiops/pages/ObservabilityPage';
import RegistryPage from './features/ai-operations/genaiops/pages/RegistryPage';
import EvaluationPage from './features/ai-operations/genaiops/pages/EvaluationPage';
import GovernancePage from './features/ai-operations/genaiops/pages/GovernancePage';
import TraceDetailPage from './features/ai-operations/genaiops/pages/TraceDetailPage';
import SessionDetailPage from './features/ai-operations/genaiops/pages/SessionDetailPage';
import PromptCreatePage from './features/ai-operations/genaiops/pages/PromptCreatePage';
import PromptDetailPage from './features/ai-operations/genaiops/pages/PromptDetailPage';
import DatasetDetailPage from './features/ai-operations/genaiops/pages/DatasetDetailPage';
import VectorExportDetailPage from './features/ai-operations/genaiops/pages/VectorExportDetailPage';
import AgentRunsPage from './features/ai-operations/agentops/pages/AgentRunsPage';
import AgentRunDetailPage from './features/ai-operations/agentops/pages/AgentRunDetailPage';
import ToolsPage from './features/ai-operations/agentops/pages/ToolsPage';
import APMPage from './features/ai-operations/agentops/pages/APMPage';
import IncidentsPage from './features/ai-operations/agentops/pages/IncidentsPage';
import IncidentDetailPage from './features/ai-operations/agentops/pages/IncidentDetailPage';
import SafetyViolationDetailPage from './features/ai-operations/agentops/pages/SafetyViolationDetailPage';

// Global Settings page
import SettingsLayout from './features/settings/components/SettingsLayout';
import {
  SubscriptionsPage,
  APIKeysPage,
  LLMProvidersPage,
  NotificationsSettingsPage,
  SDKPage
} from './features/settings/pages';
import NotificationsPage from './features/hub/pages/NotificationsPage';

// DataOps pages
import CatalogPage from './features/dataops/pages/CatalogPage';
import LineagePage from './features/dataops/pages/LineagePage';
import RunsPage from './features/dataops/pages/RunsPage';
import EventsPage from './features/dataops/pages/EventsPage';
import ImpactPage from './features/dataops/pages/ImpactPage';
import { DataOpsDashboardPage } from './features/dataops/dashboard';

// CloudOps dashboard
import { CloudOpsDashboardPage } from './features/cloudops/dashboard';

// AWS Insights component (AWS users only)
const AWSInsights = () => {
  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">AWS Program Insights</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400 transition-colors">Aggregated reporting across all partners and projects</p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 transition-colors">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 transition-colors">Partner Performance</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg transition-colors">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white transition-colors">TechSolutions Inc</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 transition-colors">2 Active Projects</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-semibold text-green-600 dark:text-green-400">92% Success Rate</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 transition-colors">$125K Total Budget</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 transition-colors">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 transition-colors">AI Model ROI</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-900 dark:text-gray-300 transition-colors">GPT-4</span>
              <span className="font-medium text-gray-900 dark:text-gray-300 transition-colors">$15K spent, 85% efficiency</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-900 dark:text-gray-300 transition-colors">Claude-3</span>
              <span className="font-medium text-gray-900 dark:text-gray-300 transition-colors">$8.5K spent, 92% efficiency</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-900 dark:text-gray-300 transition-colors">Bedrock Titan</span>
              <span className="font-medium text-gray-900 dark:text-gray-300 transition-colors">$2.5K spent, 78% efficiency</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Partners component (AWS users only)
const Partners = () => {
  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">Partner Management</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400 transition-colors">Manage and monitor AWS partner relationships</p>
      </div>
      
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg transition-colors">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 transition-colors">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white transition-colors">Active Partners</h3>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-lg font-medium text-gray-900 dark:text-white transition-colors">TechSolutions Inc</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 transition-colors">Service Integrator • Member since 2023</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 transition-colors">2 active projects, $125K total budget</p>
              </div>
              <div className="flex items-center space-x-4">
                <span className="px-3 py-1 text-sm font-medium bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 rounded-full transition-colors">
                  Active
                </span>
                <button className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  View Details
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Integrations component (Partner and Client users)

interface JiraProject {
  id: string;
  key: string;
  name: string;
}

interface JiraConfiguredProject {
  id: string;
  name: string;
  jiraProjectName?: string;
  jiraProjectKey: string;
  milestoneSource: string;
}

interface JiraData {
  connected: boolean;
  siteName?: string;
  configuredProjects?: JiraConfiguredProject[];
}

interface LensProject {
  id: string;
  name: string;
}

const Integrations: React.FC = () => {
  const { hasPermission } = usePermissions();
  const [jiraStatus, setJiraStatus] = useState('disconnected'); // disconnected, connecting, connected
  const [jiraData, setJiraData] = useState<JiraData | null>(null);
  const [showProjectSelection, setShowProjectSelection] = useState(false);
  const [projects, setProjects] = useState<LensProject[]>([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [jiraProjects, setJiraProjects] = useState<JiraProject[]>([]);
  const [selectedJiraProject, setSelectedJiraProject] = useState('');
  const [milestoneSource, setMilestoneSource] = useState('VERSIONS');
  const [configuring, setConfiguring] = useState(false);
  const isClientView = !hasPermission(PERMISSIONS.PROJECTS_CREATE) &&
    !hasPermission(PERMISSIONS.PROJECTS_EDIT) &&
    !hasPermission(PERMISSIONS.PROJECTS_MANAGE);
  
  useEffect(() => {
    checkJiraConnection();
  }, []);
  
  const checkJiraConnection = async () => {
    try {
      const data = await apiCall('/integrations/jira/status');
      if (data) {
        setJiraStatus(data.connected ? 'connected' : 'disconnected');
        setJiraData(data);
      }
    } catch (error) {
      console.error('Error checking Jira connection:', error);
    }
  };
  
  const connectJira = async () => {
    try {
      setJiraStatus('connecting');
      const data = await apiCall('/integrations/jira/connect', { method: 'POST' });
      if (data?.authUrl) {
        globalThis.location.href = data.authUrl;
      } else {
        setJiraStatus('disconnected');
      }
    } catch (error) {
      console.error('Error connecting to Jira:', error);
      setJiraStatus('disconnected');
    }
  };
  
  const disconnectJira = async () => {
    try {
      await apiCall('/integrations/jira/disconnect', { method: 'POST' });
      setJiraStatus('disconnected');
      setJiraData(null);
      setShowProjectSelection(false);
    } catch (error) {
      console.error('Error disconnecting Jira:', error);
    }
  };

  const handleSelectProject = async () => {
    try {
      // Fetch user's Lens projects
      const lensData = await apiCall('/projects');
      if (lensData && Array.isArray(lensData)) {
        setProjects(lensData);
      }

      // Fetch Jira projects from the connected workspace
      const jiraData = await apiCall('/integrations/jira/projects');
      if (jiraData && Array.isArray(jiraData)) {
        setJiraProjects(jiraData);
      }

      setShowProjectSelection(true);
    } catch (error) {
      console.error('Error fetching projects:', error);

      // Check if this is a Jira token expiration error
      if (error instanceof Error && error.message?.toLowerCase().includes('jira access token expired')) {
        if (globalThis.confirm('Your Jira access has expired. Would you like to reconnect to Jira now?')) {
          // Disconnect current integration
          await disconnectJira();
          // Trigger reconnection
          await connectJira();
        }
      } else {
        alert('Error loading projects. Please try again.');
      }
    }
  };

  const handleConfigureProject = async () => {
    if (!selectedProject || !selectedJiraProject || !milestoneSource) {
      alert('Please select both a Lens project and a Jira project, and choose a milestone source');
      return;
    }

    try {
      setConfiguring(true);

      // Find the selected Jira project's details
      const jiraProject = jiraProjects.find(p => p.id === selectedJiraProject);
      const jiraProjectKey = jiraProject?.key ?? '';
      const jiraProjectId = jiraProject?.id ?? selectedJiraProject;
      const jiraProjectName = jiraProject?.name ?? '';

      await apiCall('/integrations/jira/configure', {
        method: 'POST',
        body: JSON.stringify({
          project_id: selectedProject,
          milestone_source: milestoneSource,
          jira_project_key: jiraProjectKey,
          jira_project_id: jiraProjectId,
          jira_project_name: jiraProjectName
        })
      });
      
      alert('Project configuration saved successfully!');
      setShowProjectSelection(false);
      setSelectedProject('');
      setSelectedJiraProject('');
      // Refresh Jira status to show the newly configured project
      await checkJiraConnection();
    } catch (error) {
      console.error('Error configuring project:', error);
      alert('Failed to configure project');
    } finally {
      setConfiguring(false);
    }
  };
  
  const getTitle = () => {
    return isClientView
      ? 'Project Management Tools'
      : 'Project Management Integrations';
  };
  
  const getDescription = () => {
    return isClientView
      ? 'View and manage integrations with your project management tools'
      : 'Connect with your existing project management tools';
  };
  
  const getJiraButtonText = () => {
    if (jiraStatus === 'connecting') return 'Connecting...';
    if (jiraStatus === 'connected') return 'Connect Another Project';
    return 'Connect';
  };

  const handleRemoveProject = async (projectId: string) => {
    if (!globalThis.confirm('Unmap this project from Jira integration?')) return;

    try {
      await apiCall(`/integrations/jira/projects/${projectId}`, { method: 'DELETE' });
      // Refresh status to update the list
      checkJiraConnection();
    } catch (error) {
      console.error('Error unmapping project:', error);
      alert('Failed to unmap project');
    }
  };

  const getJiraStatusBadge = () => {
    if (jiraStatus === 'connected') {
      return (
        <span className="ml-auto px-2 py-1 text-xs bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 rounded-full transition-colors">
          Connected
        </span>
      );
    }
    if (jiraStatus === 'connecting') {
      return (
        <span className="ml-auto px-2 py-1 text-xs bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300 rounded-full transition-colors">
          Connecting...
        </span>
      );
    }
    return (
      <span className="ml-auto px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full transition-colors">
        Not Connected
      </span>
    );
  };

  if (showProjectSelection) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <button 
            onClick={() => setShowProjectSelection(false)}
            className="mb-4 text-blue-600 hover:text-blue-800 flex items-center"
          >
            ← Back to Integrations
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">Configure Jira Integration</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400 transition-colors">Select one of your projects to enable Jira integration and configure how milestones are tracked</p>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 transition-colors">
          <div className="space-y-6">
            <div>
              <label htmlFor="select-your-project" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Your Project
              </label>
              <select id="select-your-project" 
                value={selectedProject} 
                onChange={(e) => setSelectedProject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Choose a project...</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="select-jira-project" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Jira Project
              </label>
              <select id="select-jira-project"
                value={selectedJiraProject}
                onChange={(e) => setSelectedJiraProject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Choose a Jira project...</option>
                {jiraProjects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name} ({project.key})
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Select which project in your Jira workspace to sync with</p>
            </div>

            <div>
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                How should milestones be tracked?
              </span>
              
              <div className="space-y-4">
                <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                  <label className="flex items-start space-x-3" aria-label="Versions (Releases)">
                    <input
                      type="radio"
                      name="milestoneSource"
                      value="VERSIONS"
                      checked={milestoneSource === 'VERSIONS'}
                      onChange={(e) => setMilestoneSource(e.target.value)}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">Versions (Releases)</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Use Jira Versions (Releases) as milestones. Each Version will include a start and end date if configured. 
                        Project start/end will be calculated from the earliest and latest Version dates.
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                        Best for release-driven teams organizing work around planned releases or product milestones.
                      </div>
                    </div>
                  </label>
                </div>

                <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                  <label className="flex items-start space-x-3" aria-label="Sprints">
                    <input
                      type="radio"
                      name="milestoneSource"
                      value="SPRINTS"
                      checked={milestoneSource === 'SPRINTS'}
                      onChange={(e) => setMilestoneSource(e.target.value)}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">Sprints</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Use Jira Sprints as milestones. Each Sprint will have a start and end date from the board. 
                        Project start/end will be calculated from the earliest and latest Sprint dates.
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                        Best for sprint-driven agile teams running Scrum and organizing work in time-boxed sprints.
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={handleConfigureProject}
                disabled={!selectedProject || configuring}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
              >
                {configuring ? 'Configuring...' : 'Configure Integration'}
              </button>
              <button
                onClick={() => setShowProjectSelection(false)}
                className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">{getTitle()}</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400 transition-colors">{getDescription()}</p>
      </div>
      
      {isClientView && jiraStatus === 'disconnected' && (
        <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 transition-colors">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <FiInfo className="w-4 h-4 text-white" />
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-800 dark:text-blue-200 transition-colors">
                Connect your Jira workspace to sync project data, milestones, and track progress automatically.
              </p>
            </div>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 transition-colors">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">J</span>
            </div>
            <h3 className="ml-3 text-lg font-medium text-gray-900 dark:text-white transition-colors">Jira</h3>
            {getJiraStatusBadge()}
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 transition-colors">
            Sync project tasks, epics, and milestones. Track progress and timelines automatically.
          </p>
          {jiraStatus === 'connected' && jiraData && (
            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Connected to:</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{jiraData.siteName || 'Jira Workspace'}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={handleSelectProject}
                    className="w-8 h-8 bg-blue-500 text-white rounded-full hover:bg-blue-600 flex items-center justify-center text-lg font-bold"
                    title="Add project"
                  >
                    +
                  </button>
                  <button
                    onClick={disconnectJira}
                    className="w-8 h-8 bg-red-500 text-white rounded-full hover:bg-red-600 flex items-center justify-center text-lg font-bold"
                    title="Disconnect from Jira"
                  >
                    -
                  </button>
                </div>
              </div>
              {(jiraData.configuredProjects?.length ?? 0) > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-xs text-gray-600 dark:text-gray-400">Configured projects:</p>
                  {jiraData.configuredProjects?.map(project => (
                    <div key={project.id} className="flex items-center justify-between bg-white dark:bg-gray-600 p-2 rounded">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{project.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          → Jira: {project.jiraProjectName || project.jiraProjectKey} ({project.jiraProjectKey})
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Tracking: {project.milestoneSource}</p>
                      </div>
                      <button
                        onClick={() => handleRemoveProject(project.id)}
                        className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
                        title="Unmap project from Jira"
                      >
                        Unmap
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <button 
            onClick={connectJira}
            disabled={jiraStatus === 'connecting'}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400 transition-colors"
          >
            {getJiraButtonText()}
          </button>
        </div>
        
        <div className="bg-gray-100 dark:bg-gray-700 shadow rounded-lg p-6 transition-colors opacity-60">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-gray-400 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">A</span>
            </div>
            <h3 className="ml-3 text-lg font-medium text-gray-500 dark:text-gray-400 transition-colors">Aha!</h3>
            <span className="ml-auto px-2 py-1 text-xs bg-orange-100 dark:bg-orange-900/50 text-orange-600 dark:text-orange-400 rounded-full transition-colors">
              Coming Soon
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-500 mb-4 transition-colors">
            {isClientView
              ? 'Access roadmaps and feature planning from Aha!'
              : 'Import roadmaps and feature planning'
            }
          </p>
          <button className="w-full px-4 py-2 bg-gray-400 text-gray-600 rounded-md cursor-not-allowed transition-colors" disabled>
            Coming Soon
          </button>
        </div>
        
        <div className="bg-gray-100 dark:bg-gray-700 shadow rounded-lg p-6 transition-colors opacity-60">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-gray-400 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">A</span>
            </div>
            <h3 className="ml-3 text-lg font-medium text-gray-500 dark:text-gray-400 transition-colors">Asana</h3>
            <span className="ml-auto px-2 py-1 text-xs bg-orange-100 dark:bg-orange-900/50 text-orange-600 dark:text-orange-400 rounded-full transition-colors">
              Coming Soon
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-500 mb-4 transition-colors">
            {isClientView
              ? 'View tasks and team collaboration from Asana'
              : 'Sync tasks and team collaboration'
            }
          </p>
          <button className="w-full px-4 py-2 bg-gray-400 text-gray-600 rounded-md cursor-not-allowed transition-colors" disabled>
            Coming Soon
          </button>
        </div>
      </div>
      
      {isClientView && (
        <div className="mt-8 bg-gray-50 dark:bg-gray-800 rounded-lg p-6 transition-colors">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 transition-colors">Need Additional Integrations?</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 transition-colors">
            If you need additional project management tool integrations or changes to existing ones, please contact your Aivilo partner team.
          </p>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
            Contact Aivilo Support
          </button>
        </div>
      )}
    </div>
  );
};

// Communications component (Client users only)
const Communications = () => {
  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">Project Updates</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400 transition-colors">Stay informed about your project progress and milestones</p>
      </div>
      
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 transition-colors">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="ml-4 flex-1">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white transition-colors">Development Phase 1 Progress</h3>
                <span className="text-sm text-gray-500 dark:text-gray-400 transition-colors">2 hours ago</span>
              </div>
              <p className="mt-2 text-gray-600 dark:text-gray-300 transition-colors">
                We've completed the core AI model integration and are now working on the user interface components. 
                The chatbot is responding well to test queries with 95% accuracy.
              </p>
              <div className="mt-4 flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400 transition-colors">From: Partner Team</span>
                <button className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors">Reply</button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 transition-colors">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="ml-4 flex-1">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white transition-colors">Budget Update</h3>
                <span className="text-sm text-gray-500 dark:text-gray-400 transition-colors">1 day ago</span>
              </div>
              <p className="mt-2 text-gray-600 dark:text-gray-300 transition-colors">
                Your project has reached 65% budget utilization. We're on track with the timeline and 
                expect to complete within the allocated budget.
              </p>
              <div className="mt-4 flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400 transition-colors">From: Finance Team</span>
                <button className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors">View Details</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper component to redirect /assessments preserving query params
const AssessmentRedirect = () => {
  const [searchParams] = useSearchParams();
  const assessmentId = searchParams.get('assessment_id');
  const redirectPath = assessmentId
    ? `/hub/assessments?assessment_id=${assessmentId}`
    : '/hub/assessments';
  return <Navigate to={redirectPath} replace />;
};

// Hub route wrapper that conditionally requires authentication
// Allows share token access to /hub/assessments without auth
const HubRouteWrapper = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { loading } = useAuth();

  // Special case: /hub/assessments handles its own auth (auto-login, token validation)
  // Bypass all wrapper checks and let AssessmentPage manage authentication
  if (location.pathname === '/hub/assessments') {
    return children;
  }

  // Show loading state while auth is initializing for other routes
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // All other hub routes require authentication
  return <ProtectedRoute>{children}</ProtectedRoute>;
};

// Root redirect component - handles share links and authenticated users separately
const RootRedirect = () => {
  const [searchParams] = useSearchParams();

  // Check if this is an assessment share link
  const assessmentId = searchParams.get('assessment_id');
  const token = searchParams.get('token');

  // If share link, redirect directly to /hub/assessments (bypasses ProtectedRoute)
  // The AssessmentPage will handle the auto-login with the share token
  if (assessmentId && token) {
    return <Navigate to={`/hub/assessments?assessment_id=${assessmentId}&token=${token}`} replace />;
  }

  // For authenticated users without share token, use protected redirect
  return (
    <ProtectedRoute>
      <DefaultRedirect />
    </ProtectedRoute>
  );
};

// Helper component to determine default redirect based on permissions (authenticated users only)
const DefaultRedirect = () => {
  const { isAdmin } = usePermissions();
  const { isAWSUser } = useModuleAccess();

  if (isAdmin) {
    return <Navigate to="/management" replace />;
  }

  if (isAWSUser()) {
    return <Navigate to="/hub" replace />;
  }

  // For other users, redirect to hub (projects view if they have permission)
  return <Navigate to="/hub" replace />;
};

// Layout wrapper with navigation
const Layout = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      <Navigation />
      <main>
        {children}
      </main>
    </div>
  );
};

// Legacy redirect: /genaiops/* → /ai-operations/*
const GenAIOpsRedirect = () => {
  const location = useLocation();
  const subPath = location.pathname.replace(/^\/genaiops/, '');
  const search = location.search;
  return <Navigate to={`/ai-operations${subPath}${search}`} replace />;
};

// Legacy redirect: /agentops/* → /ai-operations/*
const AgentOpsRedirect = () => {
  const location = useLocation();
  const subPath = location.pathname.replace(/^\/agentops/, '');
  const search = location.search;
  // Map agentops-specific paths
  if (subPath === '/memory') return <Navigate to="/ai-operations/memory" replace />;
  if (subPath === '/safety') return <Navigate to="/ai-operations/incidents?tab=safety" replace />;
  return <Navigate to={`/ai-operations${subPath}${search}`} replace />;
};

// Main App Content Component
const AppContent = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const { hasPermission, isAdmin } = usePermissions();
  const { isAWSUser } = useModuleAccess();

  // Must be before any early returns to satisfy Rules of Hooks.
  // Save redirect_docs synchronously so it survives RootRedirect navigation.
  const redirectDocs = new URLSearchParams(location.search).get('redirect_docs');
  if (redirectDocs) {
    sessionStorage.setItem('docs_redirect', redirectDocs);
  }
  useEffect(() => {
    if (loading || !user) return;
    const target = sessionStorage.getItem('docs_redirect');
    if (target) {
      sessionStorage.removeItem('docs_redirect');
      const jwt = localStorage.getItem('token');
      if (jwt) {
        globalThis.location.href = `${target}?token=${encodeURIComponent(jwt)}`;
      }
    }
  }, [user, loading]);

  // Check if current path is a password management route
  const isPasswordRoute = ['/set-password', '/reset-password', '/forgot-password'].includes(location.pathname);

  // Don't show loading spinner on login page - the login form has its own spinner
  const isLoginPage = location.pathname === '/login';

  if (loading && !isLoginPage) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Show password management routes without authentication
  if (isPasswordRoute) {
    return (
      <Routes>
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
      </Routes>
    );
  }

  // If not authenticated and not on login or onboarding page, show login
  // Exception: Allow shared assessment links and user approval links
  const searchParams = new URLSearchParams(location.search);
  const assessmentId = searchParams.get('assessment_id');
  const token = searchParams.get('token');
  const isSharedAssessmentLink = assessmentId && token; // Check for both assessment_id and token params
  const isApprovalLink = location.pathname === '/approve-user';

  // Don't redirect if:
  // 1. Already on login page
  // 2. On assessment onboarding page (old legacy route, kept for backward compatibility)
  // 3. On approval link
  // 4. On signup page
  // 5. Has share token (will be handled by RootRedirect and AssessmentPage auto-login)
  // 6. On /hub/assessments with share token (during anonymous session setup)
  const isPublicRoute = location.pathname === '/login' ||
                       location.pathname === '/assessments/onboard' ||
                       location.pathname === '/assessments/azure' ||
                       location.pathname === '/signup' ||
                       location.pathname === '/auth/sso/callback' ||
                       location.pathname === '/marketplace/register' ||
                       location.pathname === '/eula' ||
                       isApprovalLink ||
                       isSharedAssessmentLink ||
                       (location.pathname === '/hub/assessments' && token);

  if (!user && !isPublicRoute) {
    return <Navigate to="/login" replace />;
  }

  let loginRedirectPath: string;
  if (isAdmin) loginRedirectPath = '/management';
  else if (isAWSUser()) loginRedirectPath = '/hub';
  else if (hasPermission(PERMISSIONS.PROJECTS_VIEW)) loginRedirectPath = '/hub/projects';
  else loginRedirectPath = '/hub';

  return (
    <Routes>
      <Route path="/login" element={
        user ? (
          <Navigate to={loginRedirectPath} replace />
        ) : <Login />
      } />

      {/* Public assessment onboarding routes - no authentication required */}
      <Route path="/assessments/onboard" element={<AssessmentPage />} />
      <Route path="/assessments/azure" element={<AssessmentPage />} />

      {/* User approval page - no authentication required */}
      <Route path="/approve-user" element={<ApproveUser />} />

      {/* Sign up page - no authentication required */}
      <Route path="/signup" element={<SignUp />} />

      {/* SSO callback page - no authentication required */}
      <Route path="/auth/sso/callback" element={<SSOCallbackPage />} />

      {/* AWS Marketplace registration - no authentication required */}
      <Route path="/marketplace/register" element={<MarketplaceRegisterPage />} />

      {/* Public EULA page - no authentication required */}
      <Route path="/eula" element={<EulaPage />} />

      {/* AWS connection info page - shown after trial signup */}
      <Route path="/connect-aws" element={
        <ProtectedRoute>
          <Layout><ConnectAwsInfoPage /></Layout>
        </ProtectedRoute>
      } />

      {/* Default redirect based on permissions */}
      <Route path="/" element={<RootRedirect />} />

      {/* Hub Routes with nested navigation */}
      {/* Uses HubRouteWrapper to allow share token access to assessments without auth */}
      <Route path="/hub" element={
        <HubRouteWrapper>
          <Layout><HubLayout /></Layout>
        </HubRouteWrapper>
      }>
        <Route index element={<Navigate to="/hub/dashboard" replace />} />
        <Route path="overview" element={<Navigate to="/hub/dashboard" replace />} />
        <Route path="dashboard" element={<HubDashboardPage />} />
        <Route path="dashboards" element={<DashboardMarketplace />} />
        <Route path="dashboards/designer" element={<DashboardDesigner />} />
        <Route path="dashboards/designer/:dashboardId" element={<DashboardDesigner />} />
        <Route path="dashboards/:dashboardId" element={<DashboardViewer />} />
        <Route path="projects/*" element={<ProjectsPage />} />
        <Route path="workloads/*" element={<WorkloadsPage />} />
        <Route path="support" element={<Support />} />
        <Route path="assessments" element={<AssessmentPage />} />
        <Route path="users" element={<Users />} />
        <Route path="clients" element={<Clients />} />
        {/* Legacy route - redirect to Settings */}
        <Route path="aws-environments" element={<Navigate to="/settings/aws-environments" replace />} />
        <Route path="tasks" element={<TaskExecutionsPage />} />
        <Route path="funding" element={<FundingPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="trial-info" element={<TrialInfoPage />} />
      </Route>

      {/* Health Dashboard Routes - permission-based (AWS_ACCESS_*, PARTNERS_*) */}
      <Route path="/health" element={
        <ProtectedRoute
          permissions={[PERMISSIONS.AWS_ACCESS_VIEW, PERMISSIONS.AWS_ACCESS_MANAGE, PERMISSIONS.PARTNERS_VIEW, PERMISSIONS.PARTNERS_MANAGE]}
          requireAll={false}
        >
          <Layout><HealthLayout /></Layout>
        </ProtectedRoute>
      }>
        <Route index element={<HealthDashboardPage />} />
      </Route>

      {/* CloudOps Routes with nested navigation */}
      <Route path="/cloudops" element={
        <ProtectedRoute
          permissions={[PERMISSIONS.PLATFORMOPS_MANAGE, PERMISSIONS.FINANCE_VIEW, PERMISSIONS.FINANCE_EDIT]}
          requireAll={false}
        >
          <Layout><CloudOpsLayout /></Layout>
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/cloudops/overview" replace />} />
        <Route path="overview" element={<CloudOpsDashboardPage />} />
        <Route path="finops" element={
          <ProtectedRoute permission={PERMISSIONS.FINANCE_VIEW}>
            <FinOpsPage />
          </ProtectedRoute>
        } />
        <Route path="anomalies" element={<AnomaliesPage />} />
        <Route path="tagging" element={<TaggingPage />} />
        <Route path="guardrails" element={<GuardrailsPage />} />
        <Route path="cost-recommendations" element={<CostRecommendationsPage />} />
        <Route path="compute" element={<ComputeOptimizationPage />} />
        <Route path="endpoints" element={<EnvironmentsPage />} />
        <Route path="endpoints/:environmentId" element={<MonitoringEnvironmentDetailPage />} />
        <Route path="guardrails/environments" element={<EnvironmentsManagementPage />} />
        <Route path="guardrails/environments/:environmentId" element={<EnvironmentDetailsPage />} />
        <Route path="guardrails/environments/:environmentId/resources" element={<ResourcesPage />} />
        <Route path="guardrails/scans/:scanId" element={<ScanDetailsTimelinePage />} />
        <Route path="guardrails/violations" element={<ViolationsPage />} />
        <Route path="guardrails/violations/:violationId" element={<ViolationDetailsPage />} />
        <Route path="guardrails/policies" element={<PoliciesPage />} />
        <Route path="guardrails/policies/new" element={<CreateEditPolicyPage />} />
        <Route path="guardrails/policies/:policyId/edit" element={<CreateEditPolicyPage />} />
        {/* Future routes: access, environments, etc. */}
      </Route>

      {/* AI Operations Routes (merged GenAIOps + AgentOps) */}
      <Route path="/ai-operations" element={
        <ProtectedRoute
          permissions={[PERMISSIONS.GENAIOPS_MANAGE, PERMISSIONS.AGENTOPS_MANAGE, PERMISSIONS.CLIENTS_VIEW, PERMISSIONS.CLIENTS_MANAGE]}
          requireAll={false}
        >
          <Layout><AIOperationsLayout /></Layout>
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/ai-operations/overview" replace />} />

        {/* Overview - unified dashboard */}
        <Route path="overview" element={<AIOperationsDashboardPage />} />

        {/* GenAIOps sections */}
        <Route path="observability" element={<ObservabilityPage />} />
        <Route path="registry" element={<RegistryPage />} />
        <Route path="evaluation" element={<EvaluationPage />} />
        <Route path="governance" element={<GovernancePage />} />

        {/* GenAIOps detail pages */}
        <Route path="traces/:traceId" element={<TraceDetailPage />} />
        <Route path="sessions/:sessionId" element={<SessionDetailPage />} />
        <Route path="prompts/new" element={<PromptCreatePage />} />
        <Route path="prompts/:promptId" element={<PromptDetailPage />} />
        <Route path="datasets/:datasetId" element={<DatasetDetailPage />} />
        <Route path="vector-exports/:exportId" element={<VectorExportDetailPage />} />

        {/* AgentOps sections */}
        <Route path="runs" element={<AgentRunsPage />} />
        <Route path="runs/:runId" element={<AgentRunDetailPage />} />
        <Route path="tools" element={<ToolsPage />} />
        <Route path="apm" element={<APMPage />} />
        <Route path="memory" element={<APMPage />} />
        <Route path="incidents" element={<IncidentsPage />} />
        <Route path="incidents/:incidentId" element={<IncidentDetailPage />} />
        <Route path="safety/:violationId" element={<SafetyViolationDetailPage />} />

        {/* Settings pages moved here */}
        <Route path="api-keys" element={<APIKeysPage />} />
        <Route path="llm-providers" element={<LLMProvidersPage />} />
        <Route path="sdk" element={<SDKPage />} />
      </Route>

      {/* Legacy GenAIOps routes - redirect to AI Operations */}
      <Route path="/genaiops/*" element={
        <ProtectedRoute
          permissions={[PERMISSIONS.GENAIOPS_MANAGE, PERMISSIONS.CLIENTS_VIEW, PERMISSIONS.CLIENTS_MANAGE]}
          requireAll={false}
        >
          <GenAIOpsRedirect />
        </ProtectedRoute>
      } />

      {/* Legacy AgentOps routes - redirect to AI Operations */}
      <Route path="/agentops/*" element={
        <ProtectedRoute
          permissions={[PERMISSIONS.AGENTOPS_MANAGE, PERMISSIONS.CLIENTS_VIEW, PERMISSIONS.CLIENTS_MANAGE]}
          requireAll={false}
        >
          <AgentOpsRedirect />
        </ProtectedRoute>
      } />

      {/* DataOps Routes with nested navigation */}
      <Route path="/dataops" element={
        <ProtectedRoute
          module="dataops"
          permissions={[PERMISSIONS.DATAOPS_MANAGE]}
          requireAll={false}
        >
          <Layout><DataOpsLayout /></Layout>
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/dataops/overview" replace />} />
        <Route path="overview" element={<DataOpsDashboardPage />} />
        <Route path="catalog" element={<CatalogPage />} />
        <Route path="lineage" element={<LineagePage />} />
        <Route path="runs" element={<RunsPage />} />
        <Route path="events" element={<EventsPage />} />
        <Route path="impact" element={<ImpactPage />} />
      </Route>

      {/* Global Settings Pages */}
      <Route path="/settings" element={
        <ProtectedRoute
          permissions={[
            PERMISSIONS.GENAIOPS_MANAGE,
            PERMISSIONS.CLIENTS_VIEW,
            PERMISSIONS.CLIENTS_MANAGE,
            PERMISSIONS.PLATFORMOPS_MANAGE,
            PERMISSIONS.FINANCE_VIEW,
            PERMISSIONS.FINANCE_EDIT,
            PERMISSIONS.USERS_MANAGE,
            PERMISSIONS.USERS_VIEW,
            PERMISSIONS.USERS_CREATE
          ]}
          requireAll={false}
        >
          <Layout><SettingsLayout /></Layout>
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/settings/subscriptions" replace />} />
        <Route path="subscriptions" element={<SubscriptionsPage />} />
        <Route path="notifications" element={<NotificationsSettingsPage />} />
        <Route path="access-management" element={<AccessManagementPage />} />
        <Route path="aws-environments" element={<AWSEnvironmentsPage />} />
        {/* Legacy settings routes - redirect to AI Operations */}
        <Route path="api-keys" element={<Navigate to="/ai-operations/api-keys" replace />} />
        <Route path="llm-providers" element={<Navigate to="/ai-operations/llm-providers" replace />} />
        <Route path="judge-rubrics" element={<Navigate to="/ai-operations/evaluation?tab=judge-rubrics" replace />} />
        <Route path="sdk" element={<Navigate to="/ai-operations/sdk" replace />} />
      </Route>

      {/* Notifications Page - Redirect to Hub */}
      <Route path="/notifications" element={
        <ProtectedRoute>
          <Navigate to="/hub/notifications" replace />
        </ProtectedRoute>
      } />

      <Route path="/management" element={
        <ProtectedRoute permission={PERMISSIONS.SYSTEM_ADMIN}>
          <Layout><AdminPage /></Layout>
        </ProtectedRoute>
      } />

      {/* Legacy Access Management route - redirect to Settings */}
      <Route path="/access-management" element={
        <ProtectedRoute>
          <Navigate to="/settings/access-management" replace />
        </ProtectedRoute>
      } />

      {/* Integrations Routes */}
      <Route path="/integrations" element={
        <ProtectedRoute>
          <Layout><Integrations /></Layout>
        </ProtectedRoute>
      } />

      {/* Legacy routes for backward compatibility - redirect to Hub */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Navigate to="/hub/dashboard" replace />
        </ProtectedRoute>
      } />

      <Route path="/projects/*" element={
        <ProtectedRoute>
          <Navigate to="/hub/projects" replace />
        </ProtectedRoute>
      } />

      <Route path="/assessments" element={
        <ProtectedRoute>
          <AssessmentRedirect />
        </ProtectedRoute>
      } />

      <Route path="/finance" element={
        <ProtectedRoute>
          <Navigate to="/cloudops" replace />
        </ProtectedRoute>
      } />

      {/* Legacy funding route - redirect to Hub */}
      <Route path="/funding" element={
        <ProtectedRoute>
          <Navigate to="/hub/funding" replace />
        </ProtectedRoute>
      } />

      {/* AWS-only routes */}
      <Route path="/insights" element={
        <ProtectedRoute permission={PERMISSIONS.ADMIN_VIEW}>
          <Layout><AWSInsights /></Layout>
        </ProtectedRoute>
      } />

      <Route path="/partners" element={
        <ProtectedRoute permission={PERMISSIONS.PARTNERS_VIEW}>
          <Layout><Partners /></Layout>
        </ProtectedRoute>
      } />

      <Route path="/clients" element={
        <ProtectedRoute 
          permissions={[PERMISSIONS.CLIENTS_VIEW, PERMISSIONS.CLIENTS_MANAGE]}
          requireAll={false}
        >
          <Layout><Clients /></Layout>
        </ProtectedRoute>
      } />

      <Route path="/communications" element={
        <ProtectedRoute>
          <Layout><Communications /></Layout>
        </ProtectedRoute>
      } />

      {/* Changelog */}
      <Route path="/changelog" element={
        <ProtectedRoute>
          <Layout><ChangelogPage /></Layout>
        </ProtectedRoute>
      } />

      {/* Catch all - 404 page */}
      <Route path="*" element={
        <ProtectedRoute>
          <Layout><NotFound /></Layout>
        </ProtectedRoute>
      } />
    </Routes>
  );
};

// Main App Component
function App() {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <DemoModeProvider>
            <NotificationProvider>
              <ApiHealthProvider>
                <ClientProvider>
                  <ProjectProvider>
                    <FilterProvider>
                      <Toaster position="bottom-right" />
                      <QuotaWarningBanner />
                      <AppContent />
                    </FilterProvider>
                  </ProjectProvider>
                </ClientProvider>
              </ApiHealthProvider>
            </NotificationProvider>
          </DemoModeProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
